<template><div><code-well-header>
  <nav class="d-breadcrumbs" aria-label="breadcrumb">
    <ol>
      <li class="d-breadcrumbs__item">
        <a href="#" class="d-link d-link--muted">Root</a>
      </li>
      <li class="d-breadcrumbs__item">
        <a href="#" class="d-link d-link--muted">Section</a>
      </li>
      <li class="d-breadcrumbs__item">
        <a href="#" class="d-link d-link--muted">Section</a>
      </li>
      <li class="d-breadcrumbs__item">
        <a href="#" class="d-link d-link--muted">Section</a>
      </li>
      <li class="d-breadcrumbs__item d-breadcrumbs__item--selected">
        <a href="#" class="d-link d-link--muted" aria-current="location">Current Page</a>
      </li>
    </ol>
  </nav>
</code-well-header>
<!-- <component-combinator component-name="DtBreadcrumbs" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Breadcrumbs show users where they are within a site’s hierarchy. Breadcrumbs help orient the user and allow for navigation to previous page levels. It can be appropriate to use a breadcrumb when:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The user is many levels of navigation deep</li>
<li class="d-docsite--list-element">The current page does not have its own navigation</li>
<li class="d-docsite--list-element">The user needs to quickly go back to the previous parent page</li>
</ul>
<p class="d-docsite--paragraph">Breadcrumbs should be the first page element—placed directly above the page’s title. All pages that appear in the breadcrumb should link to their respective page. The page the user is on should always be the last page listed and not linked.</p>
<h3 id="when-not-to-use" tabindex="-1" class="d-docsite--header-3">When Not to Use <a class="header-anchor d-link d-docsite--link d-link" href="#when-not-to-use" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Breadcrumbs are always treated as secondary and should not entirely replace the primary navigation. They shouldn’t be used for products that have single level navigation because they create unnecessary clutter.</p>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-well-header>
    <nav class="d-breadcrumbs" aria-label="breadcrumb">
        <ol>
            <li class="d-breadcrumbs__item">
                <a href="#" class="d-link d-link--muted">Root</a>
            </li>
            <li class="d-breadcrumbs__item">
                <a href="#" class="d-link d-link--muted">Section</a>
            </li>
            <li class="d-breadcrumbs__item">
                <a href="#" class="d-link d-link--muted">Section</a>
            </li>
            <li class="d-breadcrumbs__item">
                <a href="#" class="d-link d-link--muted">Section</a>
            </li>
            <li class="d-breadcrumbs__item d-breadcrumbs__item--selected">
                <a href="#" class="d-link d-link--muted" aria-current="location">Current Page</a>
            </li>
        </ol>
    </nav>
</code-well-header>
<code-example-tabs
htmlCode='
<nav class="d-breadcrumbs" aria-label="breadcrumb">
  <ol>
    <li class="d-breadcrumbs__item">
      <a href="#" class="d-link d-link--muted">Root</a>
    </li>
    <li class="d-breadcrumbs__item">
      <a href="#" class="d-link d-link--muted">Section</a>
    </li>
    <li class="d-breadcrumbs__item">
      <a href="#" class="d-link d-link--muted">Section</a>
    </li>
    <li class="d-breadcrumbs__item">
      <a href="#" class="d-link d-link--muted">Section</a>
    </li>
    <li class="d-breadcrumbs__item d-breadcrumbs__item--selected">
      <a href="#" class="d-link d-link--muted" aria-current="location">Current Page</a>
    </li>
  </ol>
</nav>
'
vueCode='
<dt-breadcrumbs
  :breadcrumbs="[
    {
      href: `#`,
      label: `Root`,
    },
    {
      href: `#`,
      label: `Section`,
    },
    {
      href: `#`,
      label: `Section`,
    },
    {
      href: `#`,
      label: `Section`,
    },
    {
      href: `#`,
      label: `Current Page`,
      selected: true,
    },
  ]" />
'
showHtmlWarning />
<h3 id="inverted" tabindex="-1" class="d-docsite--header-3">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h3>
<code-well-header class="d-bgc-contrast">
    <nav class="d-breadcrumbs d-breadcrumbs--inverted d-bgc-contrast" aria-label="inverted breadcrumb">
        <ol>
            <li class="d-breadcrumbs__item">
                <a href="#" class="d-link d-link--inverted">Root</a>
            </li>
            <li class="d-breadcrumbs__item">
                <a href="#" class="d-link d-link--inverted">Section</a>
            </li>
            <li class="d-breadcrumbs__item">
                <a href="#" class="d-link d-link--inverted">Section</a>
            </li>
            <li class="d-breadcrumbs__item">
                <a href="#" class="d-link d-link--inverted">Section</a>
            </li>
            <li class="d-breadcrumbs__item d-breadcrumbs__item--selected">
                <a href="#" class="d-link d-link--inverted" aria-current="location">Current Page</a>
            </li>
        </ol>
    </nav>
</code-well-header>
<code-example-tabs
htmlCode='
<nav class="d-breadcrumbs d-breadcrumbs--inverted" aria-label="inverted breadcrumb">
  <ol>
    <li class="d-breadcrumbs__item">
        <a href="#" class="d-link d-link--inverted">Root</a>
    </li>
    <li class="d-breadcrumbs__item">
        <a href="#" class="d-link d-link--inverted">Section</a>
    </li>
    <li class="d-breadcrumbs__item">
        <a href="#" class="d-link d-link--inverted">Section</a>
    </li>
    <li class="d-breadcrumbs__item">
        <a href="#" class="d-link d-link--inverted">Section</a>
    </li>
    <li class="d-breadcrumbs__item d-breadcrumbs__item--selected">
        <a href="#" class="d-link d-link--inverted" aria-current="location">Current Page</a>
    </li>
  </ol>
</nav>
'
vueCode='
<dt-breadcrumbs
  inverted
  :breadcrumbs="[
    {
      href: `#`,
      label: `Root`,
    },
    {
      href: `#`,
      label: `Section`,
    },
    {
      href: `#`,
      label: `Section`,
    },
    {
      href: `#`,
      label: `Section`,
    },
    {
      href: `#`,
      label: `Current Page`,
      selected: true,
    },
  ]" />
'
showHtmlWarning />
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="breadcrumbs" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="breadcrumbs"></component-class-table><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">HTML doesn't provide dedicated semantic elements for breadcrumbs, so we need to add a few ARIA attributes to make them accessible.</p>
<component-accessible-table component-name="breadcrumbs"></component-accessible-table></div></template>


